<template>
  <div class="card">
    <div class="card-body">
      <h4 class="header-title">{{ title }}</h4>
      <div class="d-flex align-items-center">
        <label :for="'distribution-'+blockId" class="service-select">Distribution Type</label>
        <select :id="'distribution-'+blockId" class="form-select" @change="changeDistributionType">
          <option value="package">Package</option>
          <option value="parcel">Parcel</option>
        </select>
      </div>
      <b-table-simple responsive class="mb-0">
        <b-thead>
          <b-tr>
            <b-th>Carrier</b-th>
            <b-th>{{ colTitle }}</b-th>
            <b-th>Change</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="(line, index) in table" :key="index">
            <b-td>
              <div class="carrier-identity">
                <img :src="line.logo" :alt="line.displayName" height="22" />
                <span>{{ line.displayName }}</span>
              </div>
            </b-td>
            <b-td><span v-if="line.noCurrentShipments || !hasReportValue(line.currentPeriod)" class="no-data" title="No shipments for this carrier in the selected period and distribution type.">Not enough data</span><span v-else-if="blockId === 'Usage' && isZeroReportValue(line.currentPeriod)" class="no-data">No shipments</span><span v-else>{{ line.currentPeriod }}</span></b-td>
            <b-td>
              <span v-if="line.noCurrentShipments || line.noPreviousShipments || !hasMeaningfulComparison(line.currentPeriod, line.previousPeriod, line.diff)" class="no-data" title="A comparison requires data in both periods.">—</span>
              <span v-else-if="isZeroReportValue(line.diff)" class="no-data">0%</span>
              <span v-else-if="Number(line.diff) > 0" class="text-success me-2">
                <span class="mdi mdi-arrow-up-bold"></span>
                {{ line.diff }}%
              </span>
              <span v-else-if="Number(line.diff) < 0" class="text-danger me-2">
                <span class="mdi mdi-arrow-down-bold"></span>
                {{ line.diff }}%
              </span>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
      <p v-if="table.some(line => line.noCurrentShipments)" class="no-data-note">Not enough data: no shipments match this carrier, period and distribution type.</p>
    </div>
  </div>
</template>

<script lang="ts">
import type { PeriodsDiff } from '@/typings/Shipments/PeriodsDiff'
import type { CarrierInfo } from '@/typings/Shipments/CarrierInfo'
import { ref, computed, watch, defineComponent, type ComputedRef } from 'vue'
import Carriers from '@/enums/Carriers'
import { hasReportValue, isZeroReportValue, hasMeaningfulComparison } from '@/utils/report-values'
import CarrierNames from '@/enums/CarrierNames'

export default defineComponent({
  props: {
    tableData: {
      type: Array<PeriodsDiff>,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    colTitle: {
      type: String,
      required: true,
    },
    blockId: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const distributionType = ref('package')

    const changeDistributionType = (event: Event) => {
      distributionType.value = (event.target as HTMLSelectElement).value
    }

    const getCarrierName = (name: string) =>
      ({
        [Carriers.ASENDIA]: CarrierNames.ASENDIA,
        [Carriers.BRING]: CarrierNames.BRING,
        [Carriers.CITYMAIL]: CarrierNames.CITYMAIL,
        [Carriers.DEUTSCHE_POST]: CarrierNames.DEUTSCHE_POST,
        [Carriers.LETTER_CARRIER]: CarrierNames.LETTER_CARRIER,
        [Carriers.DHL_FREIGHT]: CarrierNames.DHL_FREIGHT,
        [Carriers.DHL_EXPRESS]: CarrierNames.DHL_EXPRESS,
        [Carriers.EARLYBIRD]: CarrierNames.EARLYBIRD,
        [Carriers.POSTNORD]: CarrierNames.POSTNORD,
        [Carriers.POSTI]: CarrierNames.POSTI,
        [Carriers.UPS]: CarrierNames.UPS,
      })[name] || 'Unknown Carrier'

    const table: ComputedRef<CarrierInfo[]> = computed(() =>
      props.tableData.map((item: PeriodsDiff) => ({
        displayName: getCarrierName(item.name),
        logo: new URL(`../../../assets/images/carriers/${item.name.toLowerCase()}-logo.svg`, import.meta.url).href,
        currentPeriod: item.currentPeriod,
        previousPeriod: item.previousPeriod,
        diff: item.diff,
        noCurrentShipments: item.noCurrentShipments,
        noPreviousShipments: item.noPreviousShipments,
      })),
    )

    watch(distributionType, (newValue) => {
      emit('update:distributionType' + props.blockId, newValue)
    })

    return {
      hasReportValue,
      isZeroReportValue,
      hasMeaningfulComparison,
      distributionType,
      changeDistributionType,
      getCarrierName,
      table,
    }
  },
})
</script>

<style scoped>
.carrier-identity { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.no-data { font-size: 12px; color: #6a7d73; font-weight: 400; }
.no-data-note { font-size: 12px; color: #6a7d73; margin: 16px 0 0; }
.service-select {
  margin-right: 10px;
  white-space: nowrap;
}
</style>
